import React, { useContext, useState } from "react";
import {
  Avatar,
  CssBaseline,
  Paper,
  Box,
  Grid,
  Typography,
  Snackbar,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { AuthContext } from "../contexts/AuthContext";

/* ================= THEME ================= */
const theme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#6366f1" },
    background: {
      default: "#0b0d17",
      paper: "transparent",
    },
  },
  shape: { borderRadius: 20 },
});

export default function Authentication() {
  const { handleLogin, handleRegister } = useContext(AuthContext);

  const [formState, setFormState] = useState(0);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);

  const handleAuth = async (e) => {
    e.preventDefault();
    setError("");

    try {
      if (formState === 0) {
        await handleLogin(username, password);
      } else {
        const res = await handleRegister(name, username, password);
        setMessage(res);
        setOpen(true);
        setFormState(0);
        setName("");
        setUsername("");
        setPassword("");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong");
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Grid container sx={{ height: "100vh" }}>
        {/* ================= LEFT HERO ================= */}
        <Grid
          item
          xs={false}
          md={7}
          sx={{
            position: "relative",
            background:
              "radial-gradient(circle at top left, #6366f1, #0b0d17 60%)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(120deg, rgba(99,102,241,0.35), transparent)",
              animation: "pulse 6s infinite alternate",
            }}
          />

          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              height: "100%",
              p: 7,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              color: "white",
            }}
          >
            <Box>
              <Typography variant="h3" fontWeight={800}>
                Zoom Video Call
              </Typography>
              <Typography sx={{ mt: 1, opacity: 0.85 }}>
                Premium meetings. Zero friction.
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {["HD Video", "Live Chat", "Screen Share", "Secure"].map(
                (item) => (
                  <Box
                    key={item}
                    sx={{
                      px: 3,
                      py: 1,
                      borderRadius: 999,
                      background: "rgba(255,255,255,0.12)",
                      backdropFilter: "blur(10px)",
                      transition: "0.3s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        background: "rgba(255,255,255,0.2)",
                      },
                    }}
                  >
                    ✨ {item}
                  </Box>
                )
              )}
            </Box>

            <Typography opacity={0.6}>
              Built with MERN · Real-time · Secure
            </Typography>
          </Box>
        </Grid>

        {/* ================= RIGHT AUTH ================= */}
        <Grid item xs={12} md={5} component={Paper} elevation={0}>
          <div
            className="h-full flex items-center justify-center"
            style={{
              position: "relative",
              background:
                "radial-gradient(circle at top left, #6366f1, #0b0d17 60%)",
              overflow: "hidden",
            }}
          >
            <form
              onSubmit={handleAuth}
              className="relative w-full max-w-[360px] mx-4 p-8 rounded-2xl 
              bg-white/10 backdrop-blur-xl
              shadow-[0_20px_50px_rgba(0,0,0,0.35)]
              transition-all duration-300 hover:-translate-y-1"
            >
              {/* GLOW BACKGROUND */}
              <div
                className="absolute inset-0 rounded-2xl 
              bg-gradient-to-br from-indigo-500/30 to-purple-500/20 
              pointer-events-none z-0"
              />

              {/* CONTENT */}
              <div className="relative z-10 text-white">
                <div className="text-center mb-6">
                  <Avatar className="mx-auto mb-3 bg-indigo-500 shadow-lg">
                    <LockOutlinedIcon />
                  </Avatar>

                  <h2 className="text-2xl font-bold">
                    {formState === 0 ? "Welcome Back" : "Create Account"}
                  </h2>
                  <p className="text-sm opacity-80">
                    {formState === 0
                      ? "Login to your workspace"
                      : "Start your video journey"}
                  </p>
                </div>

                {formState === 1 && (
                  <Input
                    placeholder="Full Name"
                    value={name}
                    onChange={setName}
                  />
                )}

                <Input
                  placeholder="Username"
                  value={username}
                  onChange={setUsername}
                />

                <Input
                  placeholder="Password"
                  type="password"
                  value={password}
                  onChange={setPassword}
                />

                {error && (
                  <p className="text-red-400 text-xs text-center mt-2">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="w-full mt-5 py-2.5 rounded-xl font-semibold
                  bg-gradient-to-r from-indigo-500 to-purple-500
                  hover:shadow-xl hover:scale-[1.02] transition"
                >
                  {formState === 0 ? "Log In" : "Sign Up"}
                </button>

                <p className="text-center mt-5 text-sm opacity-80">
                  {formState === 0 ? (
                    <>
                      New here?{" "}
                      <span
                        className="text-indigo-400 cursor-pointer font-medium"
                        onClick={() => setFormState(1)}
                      >
                        Create account
                      </span>
                    </>
                  ) : (
                    <>
                      Already a member?{" "}
                      <span
                        className="text-indigo-400 cursor-pointer font-medium"
                        onClick={() => setFormState(0)}
                      >
                        Login
                      </span>
                    </>
                  )}
                </p>
              </div>
            </form>
          </div>
        </Grid>
      </Grid>

      <Snackbar
        open={open}
        autoHideDuration={4000}
        message={message}
        onClose={() => setOpen(false)}
      />
    </ThemeProvider>
  );
}

/* ================= REUSABLE INPUT ================= */
function Input({ placeholder, value, onChange, type = "text" }) {
  return (
    <div className="mt-3">
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-2.5 rounded-xl
        bg-white/20 text-white placeholder-white/60
        border border-white/20
        focus:ring-2 focus:ring-indigo-400 outline-none
        transition"
      />
    </div>
  );
}
